/**
 * Cash Flow Chart Component
 *
 * Stacked bar chart showing cash in, cash out, and net cash flow over time.
 */

import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import type { CashFlowData } from '../../../types/financialInsights';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface CashFlowChartProps {
  data: CashFlowData | undefined;
  loading: boolean;
}

export const CashFlowChart: React.FC<CashFlowChartProps> = ({
  data,
  loading,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="rectangular" width="100%" height={350} />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.cash_in.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ height: 350 }}
          >
            <Typography variant="h6" color="text.secondary">
              No Cash Flow Data Available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const chartData = {
    labels: data.cash_in.map(d => d.date),
    datasets: [
      {
        label: 'Cash In',
        data: data.cash_in.map(d => d.value),
        backgroundColor: '#4caf50',
      },
      {
        label: 'Cash Out',
        data: data.cash_out.map(d => d.value),
        backgroundColor: '#f44336',
      },
      {
        label: 'Net Cash Flow',
        data: data.net_cash_flow.map(d => d.value),
        backgroundColor: theme.palette.primary.main,
      },
    ],
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Cash Flow Analysis
        </Typography>
        <Box sx={{ height: 350 }}>
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default CashFlowChart;
